import axios from 'axios';

const API_BASE_URL = 'https://ourzestapp.online/oz_backend/mechanics_module';
let authToken = null;
let currentUser = null;
let currentShop = null;

export const setAuthData = (user, shop) => {
  currentUser = user;
  currentShop = shop;
  if (user && user.token) authToken = user.token;
};

const getHeaders = () => ({
  'Content-Type': 'application/json',
  ...(authToken && { 'Authorization': `Bearer ${authToken}` })
});

const apiRequest = async (endpoint, data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/${endpoint}`, data, { headers: getHeaders() });
    return response.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    return { status: 'error', message: error.message, data: [] };
  }
};

// Helper to safely get number from API response
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') return defaultValue;
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

// Helper to safely get array from API response
const safeArray = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response.data && Array.isArray(response.data)) return response.data;
  if (response.data && response.data.data && Array.isArray(response.data.data)) return response.data.data;
  if (Array.isArray(response.data)) return response.data;
  return [];
};

// Fetch all dashboard data
export const fetchDashboardData = async (filters = {}) => {
  if (!currentUser) throw new Error('User not authenticated');
  const shopId = currentShop?.shop_id || currentUser?.shop_id;
  if (!shopId) return { stats: {}, trend: { labels: [], values: [] }, recent_sales: [], low_stock: [] };

  const dashboardData = { stats: {}, trend: { labels: [], values: [] }, recent_sales: [], low_stock: [] };

  try {
    // Fetch data in parallel
    const [customersRes, suppliersRes, productsRes, todaySalesRes, monthSalesRes, purchasesRes] = await Promise.all([
      apiRequest('mechanics_customers.php', { action: 'get_records', shop_id: shopId, username: currentUser?.username }),
      apiRequest('mechanics_suppliers.php', { action: 'list', shop_id: shopId }),
      apiRequest('mechanics_products.php', { action: 'get_records', shop_id: shopId, username: currentUser?.username }),
      apiRequest('mechanics_sell.php', { action: 'get_records', shop_id: shopId, date: new Date().toISOString().split('T')[0] }),
      apiRequest('mechanics_sell.php', { action: 'get_records', shop_id: shopId, start_date: filters.startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], end_date: filters.endDate || new Date().toISOString().split('T')[0] }),
      apiRequest('mechanics_purchases.php', { action: 'get_records', shop_id: shopId, start_date: filters.startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], end_date: filters.endDate || new Date().toISOString().split('T')[0] })
    ]);

    // Parse data safely
    const productList = safeArray(productsRes);
    const salesList = safeArray(monthSalesRes);
    const todayList = safeArray(todaySalesRes);
    const purchasesList = safeArray(purchasesRes);
    
    // Calculate stats with safe numbers
    const totalCustomers = safeNumber(customersRes?.total || customersRes?.data?.length || 0);
    const totalSuppliers = safeNumber(suppliersRes?.suppliers?.length || suppliersRes?.data?.length || 0);
    
    const todaySalesTotal = todayList.reduce((sum, sale) => sum + safeNumber(sale.grand_total), 0);
    const todaySalesCount = todayList.length;
    
    const monthSalesTotal = salesList.reduce((sum, sale) => sum + safeNumber(sale.grand_total), 0);
    const monthPurchasesTotal = purchasesList.reduce((sum, p) => sum + safeNumber(p.grand_total), 0);
    
    // =====================================================
    // FIX: INVENTORY VALUE CALCULATION
    // =====================================================
    // Calculate inventory value correctly:
    // - For products with packing: use cost_per_pack * quantity (where quantity is in packs)
    // - For products without packing: use cost_price (cost per unit) * quantity
    // - Only include products with quantity > 0 (in stock)
    // =====================================================
    let inventoryValue = 0;
    let totalProducts = productList.length;
    
    productList.forEach(p => {
      const quantity = safeNumber(p.quantity);
      
      // Only include products that are in stock (quantity > 0)
      if (quantity <= 0) return;
      
      // Check if product has packing information
      const costPerPack = safeNumber(p.cost_per_pack);
      const costPerUnit = safeNumber(p.cost_price || p.cost_per_unit);
      const itemsPerPack = safeNumber(p.items_per_pack, 1);
      
      // Calculate value based on available cost data
      let value = 0;
      
      if (costPerPack > 0 && itemsPerPack > 1) {
        // Product with packing: cost_per_pack * quantity (quantity is in packs)
        // Also add cost of partial packs if any
        const fullPacks = Math.floor(quantity);
        const partialItems = quantity - fullPacks;
        const partialValue = partialItems * (costPerPack / itemsPerPack);
        value = (fullPacks * costPerPack) + partialValue;
      } else if (costPerUnit > 0) {
        // Simple product: cost_per_unit * quantity
        value = costPerUnit * quantity;
      } else if (costPerPack > 0) {
        // Fallback: cost_per_pack * quantity
        value = costPerPack * quantity;
      }
      
      inventoryValue += value;
    });
    
    const lowStockCount = productList.filter(p => safeNumber(p.quantity) <= safeNumber(p.quantity_alert, 5)).length;
    
    dashboardData.stats = {
      total_customers: totalCustomers,
      total_suppliers: totalSuppliers,
      total_products: totalProducts,
      today_sales: todaySalesTotal,
      today_sales_count: todaySalesCount,
      month_sales: monthSalesTotal,
      month_sales_count: salesList.length,
      total_purchases: monthPurchasesTotal,
      inventory_value: inventoryValue,
      low_stock_count: lowStockCount,
    };

    // Sales trend
    const salesByDate = {};
    salesList.forEach(sale => {
      const date = sale.date;
      if (date) {
        salesByDate[date] = (salesByDate[date] || 0) + safeNumber(sale.grand_total);
      }
    });
    const dates = Object.keys(salesByDate).sort();
    dashboardData.trend = { 
      labels: dates.map(d => {
        const date = new Date(d);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }), 
      values: dates.map(d => salesByDate[d]) 
    };
    dashboardData.recent_sales = salesList.slice(0, 10);
    dashboardData.low_stock = productList
      .filter(p => safeNumber(p.quantity) <= safeNumber(p.quantity_alert, 5))
      .slice(0, 20)
      .map(p => ({ 
        id: p.id, 
        product_name: p.product_name || 'Unknown', 
        batch_no: p.batch_no || '-', 
        quantity: p.quantity, 
        unit: p.unit || 'pcs', 
        quantity_alert: p.quantity_alert || 5
      }));

    return dashboardData;
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    return dashboardData;
  }
};

// Fetch Services Data
export const fetchServicesData = async (shopId, username, filters = {}) => {
  try {
    const response = await apiRequest('mechanics_service.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    const services = safeArray(response);
    return {
      total: services.length,
      pending: services.filter(s => s.status === 'pending').length,
      in_progress: services.filter(s => s.status === 'in_progress').length,
      completed: services.filter(s => s.status === 'completed').length,
      total_revenue: services.reduce((sum, s) => sum + safeNumber(s.grand_total), 0),
      recent: services.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching services:', error);
    return { total: 0, pending: 0, in_progress: 0, completed: 0, total_revenue: 0, recent: [] };
  }
};

// Fetch Mobchange Data
export const fetchMobchangeData = async (shopId, username, filters = {}) => {
  try {
    const response = await apiRequest('mechanics_mobchange.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
    });
    const mobchanges = safeArray(response);
    return {
      total: mobchanges.length,
      pending: mobchanges.filter(m => m.status === 'pending').length,
      completed: mobchanges.filter(m => m.status === 'completed' || m.status === 'delivered').length,
      total_revenue: mobchanges.reduce((sum, m) => sum + safeNumber(m.grand_total), 0),
      recent: mobchanges.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching mobchanges:', error);
    return { total: 0, pending: 0, completed: 0, total_revenue: 0, recent: [] };
  }
};

// Fetch Returns Data - Now works with the fixed backend
export const fetchReturnsData = async (shopId, username, filters = {}) => {
  try {
    console.log('Fetching returns with filters:', filters);
    
    const response = await apiRequest('mechanics_return.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate || '',  // Now backend accepts both start_date/end_date AND date_from/date_to
      end_date: filters.endDate || '',
    });
    
    console.log('Returns API Response:', response);
    
    // Parse the response - backend returns { status: 'success', data: [...], total: X }
    let returns = [];
    let totalCount = 0;
    
    if (response && response.status === 'success') {
      if (response.data && Array.isArray(response.data)) {
        returns = response.data;
        totalCount = response.total || returns.length;
      } else if (Array.isArray(response)) {
        returns = response;
        totalCount = returns.length;
      }
    } else if (Array.isArray(response)) {
      returns = response;
      totalCount = returns.length;
    } else if (response && response.data && Array.isArray(response.data)) {
      returns = response.data;
      totalCount = response.total || returns.length;
    }
    
    console.log(`Found ${returns.length} returns, total count: ${totalCount}`);
    
    // Calculate total amount
    const totalAmount = returns.reduce((sum, r) => {
      const amount = safeNumber(r.refund_amount) || safeNumber(r.grand_total) || 0;
      return sum + amount;
    }, 0);
    
    return {
      total: totalCount,
      total_amount: totalAmount,
      recent: returns.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching returns:', error);
    return { total: 0, total_amount: 0, recent: [] };
  }
};

// Fetch Employees Data
export const fetchEmployeesData = async (shopId, username) => {
  try {
    const response = await apiRequest('mechanics_employees.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
    });
    const employees = safeArray(response);
    
    let payments = [];
    try {
      const paymentsRes = await apiRequest('mechanics_employees.php', {
        action: 'get_payments',
        shop_id: shopId,
        limit: 100,
      });
      payments = safeArray(paymentsRes);
    } catch (e) {
      console.error('Error fetching payments:', e);
    }
    
    return {
      total: employees.length,
      active: employees.filter(e => e.status === 'active').length,
      total_salary_paid: payments.reduce((sum, p) => sum + safeNumber(p.amount), 0),
      recent: employees.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching employees:', error);
    return { total: 0, active: 0, total_salary_paid: 0, recent: [] };
  }
};

// Fetch Transactions Data
export const fetchTransactionsData = async (shopId, filters = {}) => {
  try {
    const response = await apiRequest('mechanics_transactions.php', {
      action: 'get_transactions',
      shop_id: shopId,
      start_date: filters.startDate,
      end_date: filters.endDate,
      limit: 100,
    });
    const transactions = safeArray(response);
    return {
      customer: transactions.filter(t => t.reference_type === 'customer'),
      supplier: transactions.filter(t => t.reference_type === 'supplier'),
      employee: transactions.filter(t => t.reference_type === 'employee'),
      expense: transactions.filter(t => t.reference_type === 'expense'),
    };
  } catch (error) {
    console.error('Error fetching transactions:', error);
    return { customer: [], supplier: [], employee: [], expense: [] };
  }
};

// Fetch Purchases Data
export const fetchPurchasesData = async (shopId, username, filters = {}) => {
  try {
    const response = await apiRequest('mechanics_purchases.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    const purchases = safeArray(response);
    return {
      total: purchases.length,
      total_amount: purchases.reduce((sum, p) => sum + safeNumber(p.grand_total), 0),
      recent: purchases.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching purchases:', error);
    return { total: 0, total_amount: 0, recent: [] };
  }
};